import { useEffect, useState } from 'react';
import { MapPin, CloudSun } from 'lucide-react';
import { weatherDesc } from './constants';

export const useParisWeather = () => {
    const [weather, setWeather] = useState(null);
    const [forecast, setForecast] = useState([]);
    const [parisTime, setParisTime] = useState('');

    useEffect(() => {
        const fetchWeather = async () => {
            try {
                const res = await fetch('https://api.open-meteo.com/v1/forecast?latitude=48.8566&longitude=2.3522&current=temperature_2m,weather_code,wind_speed_10m&daily=temperature_2m_max,temperature_2m_min,weather_code,precipitation_probability_max&timezone=Europe/Paris&forecast_days=7');
                const data = await res.json();
                if (data.current) {
                    setWeather({
                        temp: Math.round(data.current.temperature_2m),
                        code: data.current.weather_code,
                        wind: Math.round(data.current.wind_speed_10m),
                    });
                }
                if (data.daily) {
                    const days = data.daily.time.map((date, i) => ({
                        date,
                        tempMax: Math.round(data.daily.temperature_2m_max[i]),
                        tempMin: Math.round(data.daily.temperature_2m_min[i]),
                        code: data.daily.weather_code[i],
                        precipProb: data.daily.precipitation_probability_max[i],
                    }));
                    setForecast(days);
                }
            } catch (err) {
                console.warn('Weather fetch failed:', err.message);
            }
        };
        fetchWeather();
        const weatherInterval = setInterval(fetchWeather, 600000);

        const updateTime = () => {
            const now = new Date().toLocaleString('fr-FR', {
                timeZone: 'Europe/Paris',
                hour: '2-digit',
                minute: '2-digit',
                weekday: 'short',
                day: 'numeric',
                month: 'short',
            });
            setParisTime(now);
        };
        updateTime();
        const timeInterval = setInterval(updateTime, 30000);

        return () => { clearInterval(weatherInterval); clearInterval(timeInterval); };
    }, []);

    return { weather, forecast, parisTime };
};

export const WeatherWidget = ({ weather, forecast, parisTime, showForecast, setShowForecast }) => {
    if (!weather) return null;

    return (
        <div className="absolute top-3 left-4 sm:left-6 lg:left-8 z-10" data-testid="weather-widget">
            <button
                onClick={() => setShowForecast(!showForecast)}
                className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-3 py-2 text-white flex items-center gap-2 hover:bg-white/20 transition-all cursor-pointer text-sm"
                data-testid="weather-widget-btn"
            >
                <div className="text-2xl">{weatherDesc(weather.code).icon}</div>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-xl font-bold">{weather.temp}°C</span>
                        <span className="text-white/60 text-xs">{weatherDesc(weather.code).text}</span>
                    </div>
                    <div className="text-white/50 text-xs flex items-center gap-1.5 mt-0.5">
                        <MapPin className="w-3 h-3" />
                        Paris — {parisTime}
                    </div>
                </div>
                <CloudSun className="w-4 h-4 text-white/40 ml-1" />
            </button>

            {showForecast && forecast.length > 0 && (
                <div className="absolute top-full left-0 mt-2 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 w-[320px] shadow-2xl z-50" data-testid="weather-forecast-modal">
                    <div className="flex items-center justify-between mb-3">
                        <h4 className="text-white font-semibold text-sm">Prévisions Paris — 7 jours</h4>
                        <button onClick={() => setShowForecast(false)} className="text-white/40 hover:text-white text-xs">✕</button>
                    </div>
                    <div className="space-y-2">
                        {forecast.map((day, i) => {
                            const d = new Date(day.date);
                            const dayName = i === 0 ? "Auj." : d.toLocaleDateString('fr-FR', { weekday: 'short' });
                            const dayNum = d.getDate();
                            const desc = weatherDesc(day.code);
                            return (
                                <div key={day.date} className={`flex items-center gap-3 py-2 px-3 rounded-xl ${i === 0 ? 'bg-white/10' : 'hover:bg-white/5'} transition-colors`} data-testid={`forecast-day-${i}`}>
                                    <div className="w-12 text-left">
                                        <span className="text-white/80 text-xs font-medium capitalize">{dayName}</span>
                                        <span className="text-white/40 text-xs ml-1">{dayNum}</span>
                                    </div>
                                    <div className="text-lg">{desc.icon}</div>
                                    <div className="flex-1 text-xs text-white/50 truncate">{desc.text}</div>
                                    {day.precipProb > 0 && (
                                        <span className="text-blue-300 text-xs">{day.precipProb}%</span>
                                    )}
                                    <div className="text-right w-16">
                                        <span className="text-white font-semibold text-sm">{day.tempMax}°</span>
                                        <span className="text-white/40 text-xs ml-1">{day.tempMin}°</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
